<!-- eslint-disable vue-a11y/click-events-have-key-events -->
<!-- eslint-disable vue-a11y/mouse-events-have-key-events -->
<template>
	<div class="c-Menu">
		<transition name="fade">
			<ul
				v-if="active"
				class="c-Menu__mainMenu"
				:style="{ width: `${width}px` }">
				<li
					v-for="(item, index) in parsedItems"
					:key="index"
					class="c-Menu__mainMenuItem"
					:class="menuItemClassObj(item)">
					<button
						type="button"
						:disabled="item.disabled"
						@click="triggerItem(item, index)"
						@hover="toggleSubMenu(item, index, true)"
						@mouseenter="toggleSubMenu(item, index, true)"
						@blue="toggleSubMenu(item, index, false)"
						@mouseleave="toggleSubMenu(item, index, false)"
						@mousedown.prevent>
						<p>{{ item.label }}</p>
						<i v-if="item.subMenu"></i>
						<span v-if="!item.subMenu && item.labelShortcut">{{ item.labelShortcut }}</span>
						<!-- Sub menu -->
						<transition name="fade">
							<ul
								v-if="item.subMenu"
								v-show="item.subMenuVisible"
								class="c-Menu__subMenu"
								:style="{ width: `${width}px` }">
								<li
									v-for="(sub, i) in item.subMenu"
									:key="i"
									class="c-Menu__subMenuItem"
									:class="menuItemClassObj(sub)">
									<button
										type="button"
										:disabled="sub.disabled"
										@click="triggerItem(sub, i, $event)"
										@mousedown.prevent>
										<p>{{ sub.label }}</p>
										<span v-if="sub.labelShortcut">{{ sub.labelShortcut }}</span>
									</button>
								</li>
							</ul>
						</transition>
					</button>
				</li>
			</ul>
		</transition>
	</div>
</template>

<script>
export default {
	name: 'Menu',
	props: {
		/**
     * If the Menu is active (visible)
     * @values true, false
     */
		active: {
			type: Boolean,
			required: false,
			default: () => false
		},
		/**
     * Array of options for the Menu (follows a defined structure)
     */
		items: {
			type: Array,
			required: true,
			default: () => []
		},
		/**
     * If clicking outside the Menu should close the component
     * @values true, false
     */
		closeOnClickOutside: {
			type: Boolean,
			required: false,
			default: () => true
		},
		/**
     * If clicking on an option of the Menu should close the component
     * @values true, false
     */
		closeOnMenuClick: {
			type: Boolean,
			required: false,
			default: () => true
		},
		/**
     * Fixed width (in px) for both main and sub menus
     */
		width: {
			type: Number,
			required: false,
			default: () => undefined
		}
	},
	data () {
		return {
			parsedItems: []
		}
	},
	watch: {
		items: {
			handler (newVal) {
				this.parsedItems = newVal.map(item => {
					const parsedItem = {
						...item
					}

					if (item.subMenu && item.subMenu.length) {
						parsedItem.subMenuVisible = false
					}

					return parsedItem
				})
			},
			immediate: true
		}
	},
	mounted () {
		if (this.closeOnClickOutside) {
			document.addEventListener('click', this.handleGeneralClick, true)
		}
	},
	beforeDestroy () {
		if (this.closeOnClickOutside) {
			document.removeEventListener('click', this.handleGeneralClick, true)
		}
	},
	methods: {
		handleGeneralClick (ev) {
			if (!this.$el.contains(ev.target)) {
				this.closeAllSubMenus()
				this.$emit('menuClose')
			}
		},
		menuItemClassObj (item) {
			return {
				divider: item.divider
			}
		},
		triggerItem (item, index, ev) {
			if (ev) {
				ev.stopPropagation()
			}
			/**
			 * Triggers when a Menu option is clicked
			 * @event menuClick
			 * @property {obj} item the option's Object
			 * @property {int} index the option array index
			 */
			this.$emit('menuClick', item, index)
			if (this.closeOnMenuClick) {
				this.closeAllSubMenus()
				/**
				 * Triggers when an action to close occurs (depends on certain flags)
				 * @event menuClose
				 */
				this.$emit('menuClose')
			}
		},
		toggleSubMenu (item, index, val) {
			if (item.subMenu && item.subMenu.length) {
				this.parsedItems[index].subMenuVisible = val
			}
		},
		closeAllSubMenus () {
			this.parsedItems.forEach((item, i) => {
				if (this.parsedItems[i].subMenu && this.parsedItems[i].subMenu.length) {
					this.parsedItems[i].subMenuVisible = false
				}
			})
		}
	}
}
</script>

<style lang="scss" scoped>
@import '../../scss/variables.scss';
.c-Menu {
  .fade-enter, .fade-leave-to {
    opacity: 0;
  }

  &__mainMenu, &__subMenu {
    list-style: none;
    margin: 0;
    padding: 0;
    width: auto;
    background-color: $un-white;
    border-radius: $border-radius;
    box-shadow: 0px 3px 6px 0px rgba(174, 185, 203, 0.6), 0px 3px 6px 0px rgba(174, 185, 203, 0.6);
    opacity: 1;
    transition: opacity $default-time $default-easing;
    width: min-content;
  }

  &__subMenu {
    position: absolute;
    top: 0;
    right: 0;
    transform: translateX(100%);
    z-index: 100;
  }

  &__mainMenuItem, &__subMenuItem {
    margin: 0;
    padding: 0;

    &.divider {
      border-bottom: 1px solid $un-n100;
    }

    button {
      width: 100%;
      height: 100%;
      border: none;
      background-color: transparent;
      min-height: 48px;
      padding: $base-padding;
      cursor: pointer;
      outline: none;
      display: flex;
      align-items: center;
      justify-content: space-between;
      position: relative;

      p, span {
        color: $un-n900;
        font-size: 14px;
        font-family: $primary-font;
        font-weight: normal;
        letter-spacing: 0.25px;
        line-height: 21px;
        text-align: left;
        white-space: nowrap;
      }

      span {
        margin-left: calc(#{$base-margin} * 2);
      }

      i {
        width: 16px;
        height: 16px;
        background-size: cover;
        background-repeat: no-repeat;
        background-image: url("data:image/svg+xml,%3Csvg width='24' height='24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M15.356 11.994c0 .35-.15.7-.4.95l-4.8 4.75c-.35.35-.9.35-1.25 0a.875.875 0 010-1.25l4.35-4.35c.05-.05.05-.15 0-.2l-4.35-4.35a.875.875 0 010-1.25c.35-.35.9-.35 1.25 0l4.75 4.75c.3.25.45.6.45.95z' fill='%23343941' fill-rule='evenodd'/%3E%3C/svg%3E");
        margin-left: calc(#{$base-margin} * 2);
        margin-right: -5px;
      }

      &::-moz-focus-inner {
        border: 0;
      }

      &:not([disabled]):hover, &:not([disabled]):focus {
        background-color: $un-blue-light;
        color: $un-n900;
      }

      &:disabled {
        cursor: not-allowed;

        p, span {
          color: $un-n500;
        }
      }
    }
  }
}
</style>
